import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Autocomplete,
} from '@mui/material';
import {
  Response,
  SuccessResponse,
  Class,
  Registration,
} from '../../types/ClassTable.type';
import { apiGet, apiPost } from '../../utils/apiClient';
import { notifyAPIClient } from '../../utils/toast';
import SofLevelChip from '../common/SofLevelChip';

type AddStudentDialogProps = {
  open: boolean;
  handleClose: () => void;
  selectedClass: Class | null;
  fetchStudents: () => void;
  fetchCheckInSummary: () => void;
};

const AddStudentDialog = ({
  open,
  handleClose,
  selectedClass,
  fetchStudents,
  fetchCheckInSummary,
}: AddStudentDialogProps) => {
  const [students, setStudents] = React.useState<Registration[]>([]);
  const [selectedStudent, setSelectedStudent] =
    React.useState<Registration | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const uniqueStudents = React.useMemo(() => {
    const byUserId = new Map<number, Registration>();
    for (const student of students) {
      const current = byUserId.get(student.user.id);
      if (
        !current ||
        new Date(student.createdAt).getTime() >
          new Date(current.createdAt).getTime() ||
        (student.createdAt === current.createdAt && student.id > current.id)
      ) {
        byUserId.set(student.user.id, student);
      }
    }
    return Array.from(byUserId.values());
  }, [students]);

  const getOptionServiceName = (option: Registration): string =>
    option.subsubtype.name || option.subtype || 'Course';

  const getOptionLevel = (option: Registration): string =>
    option.serviceType === 'COURSE' ? option.courseLevel?.trim() || '' : '';

  const getOptionLabel = (option: Registration): string => {
    const serviceName = getOptionServiceName(option);
    const level = getOptionLevel(option) || 'No level';
    return `[${serviceName} - ${level}] - ${option.user.lastName} ${option.user.firstName} - ${option.user.phone || ''} - ${option.user.address || ''}`;
  };

  const fetchAllStudents = async () => {
    if (selectedClass) {
      setIsLoading(true);
      try {
        const response = await apiGet<Response<Registration>>(
          `/class/student/list-all?classId=${selectedClass.id}`,
        );
        setStudents(response.data);
      } catch (error) {
        console.error('Error fetching students:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleAddStudent = async () => {
    if (!selectedStudent) {
      notifyAPIClient('Please select a student to add.');
      return;
    } else {
      if (!selectedClass) return;
      setIsSubmitting(true);
      try {
        const response = await apiPost<SuccessResponse>('/class/student/add', {
          classId: selectedClass.id,
          registrationId: selectedStudent.id,
        });

        notifyAPIClient(response.message);
        fetchStudents();
        fetchCheckInSummary();
        handleCloseDialog();
      } catch (error) {
        console.error('Error adding student:', error);
        const apiError = error as {
          response?: { data?: { message?: string } };
        };
        notifyAPIClient(
          apiError.response?.data?.message ||
            'An error occurred while adding the student.',
        );
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleCloseDialog = () => {
    handleClose();
    setStudents([]);
    setSelectedStudent(null);
    setIsLoading(false);
    setIsSubmitting(false);
  };

  React.useEffect(() => {
    if (open) {
      fetchAllStudents();
    }
  }, [open]);

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={handleCloseDialog}>
      <DialogTitle>Add New Student</DialogTitle>
      <DialogContent>
        <Autocomplete
          options={uniqueStudents}
          loading={isLoading}
          getOptionLabel={getOptionLabel}
          renderOption={(props, option) => (
            <li {...props} key={option.user.id}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                <strong>[{getOptionServiceName(option)} -</strong>
                <SofLevelChip
                  level={getOptionLevel(option) || undefined}
                  serviceType={option.subtype}
                  emptyLabel="No level"
                />
                <strong>]</strong>{' '}
                {option.user.lastName} {option.user.firstName} -{' '}
                <em>{option.user.phone || ''}</em> -{' '}
                <em>{option.user.address || ''}</em>
              </div>
            </li>
          )}
          isOptionEqualToValue={(option, value) =>
            option?.user.id === value?.user.id
          }
          filterOptions={(options, state) =>
            options.filter((opt) => {
              const serviceName = getOptionServiceName(opt).toLowerCase();
              const level = getOptionLevel(opt).toLowerCase();
              const name =
                opt.user.lastName?.toLowerCase() +
                ' ' +
                opt.user.firstName?.toLowerCase();
              const phone = opt.user.phone?.toLowerCase() || '';
              const address = opt.user.address?.toLowerCase() || '';
              const input = state.inputValue.toLowerCase();

              return (
                serviceName.includes(input) ||
                level.includes(input) ||
                name?.includes(input) ||
                phone?.includes(input) ||
                address?.includes(input)
              );
            })
          }
          value={selectedStudent}
          onChange={(_, newValue) => setSelectedStudent(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              className="required-field"
              label="Select Student"
              placeholder="[Service name - level] - Name - Phone - Address"
              variant="standard"
              name="autocomplete"
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleAddStudent}
          disabled={isLoading || isSubmitting || !selectedStudent}
        >
          {isSubmitting ? 'Adding...' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddStudentDialog;
